import React, { useState, useRef, useMemo, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { useTranslation } from 'react-i18next';
import type { MediaTagResponse } from 'we-tree-api-client/v2';
import ChevronDown from '@/icons/ChevronDown';

import '@/styles/tag-filter-dropdown.css';

export interface TagFilterDropdownProps {
  tags: MediaTagResponse[];
  selectedTagIds: string[];
  tagMatchAnd: boolean;
  onSelectedTagsChange: (tagIds: string[]) => void;
  onTagMatchChange: (matchAll: boolean) => void;
  id?: string;
  ariaLabel?: string;
}

const TagFilterDropdown: React.FC<TagFilterDropdownProps> = ({
  tags,
  selectedTagIds,
  tagMatchAnd,
  onSelectedTagsChange,
  onTagMatchChange,
  id = 'tag-filter',
  ariaLabel
}) => {
  const { t } = useTranslation('forms');
  const [open, setOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resolvedAriaLabel = ariaLabel ?? t('tags.filterByTag');

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setTagSearch('');
    }
  }, []);

  const handleOpenAutoFocus = useCallback(
    (e: Event) => {
      if (tags.length > 0) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    },
    [tags.length]
  );

  const filteredTags = useMemo(() => {
    if (!tagSearch.trim()) return tags;
    const q = tagSearch.trim().toLowerCase();
    return tags.filter((tag) => tag.name.toLowerCase().includes(q));
  }, [tags, tagSearch]);

  const handleToggleTag = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((selectedId) => selectedId !== tagId)
      : [...selectedTagIds, tagId];
    onSelectedTagsChange(next);
  };

  const triggerLabel =
    selectedTagIds.length === 0
      ? t('tags.allTags')
      : selectedTagIds.length <= 2
        ? tags
            .filter((tag) => selectedTagIds.includes(tag.id))
            .map((tag) => tag.name)
            .join(', ')
        : t('tags.tagsCount', { count: selectedTagIds.length });

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <div className="tag-filter-dropdown">
        <div className="tag-filter-dropdown__bar">
          <ToggleGroup.Root
            type="single"
            value={tagMatchAnd ? 'and' : 'or'}
            onValueChange={(v) => v && onTagMatchChange(v === 'and')}
            className="tag-filter-dropdown__mode-slider"
            role="group"
            aria-label={t('tags.matchMode')}
          >
            <ToggleGroup.Item value="or" className="tag-filter-dropdown__mode-option">
              {t('tags.matchOr')}
            </ToggleGroup.Item>
            <ToggleGroup.Item value="and" className="tag-filter-dropdown__mode-option">
              {t('tags.matchAnd')}
            </ToggleGroup.Item>
          </ToggleGroup.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              id={id}
              className="tag-filter-dropdown__trigger"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={resolvedAriaLabel}
            >
              <span className="tag-filter-dropdown__trigger-label">{triggerLabel}</span>
              <ChevronDown className="tag-filter-dropdown__trigger-arrow" width={16} height={16} aria-hidden />
            </button>
          </Popover.Trigger>
        </div>

        <Popover.Portal>
          <Popover.Content
            className="tag-filter-dropdown__panel"
            side="bottom"
            align="start"
            sideOffset={4}
            onOpenAutoFocus={handleOpenAutoFocus}
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={id}
          >
            {tags.length > 0 && (
              <>
                <div className="tag-filter-dropdown__search-wrap">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="tag-filter-dropdown__search"
                    placeholder={t('tags.filterTagsPlaceholder')}
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label={t('tags.filterTagListAria')}
                  />
                </div>
                {selectedTagIds.length > 0 && (
                  <button
                    type="button"
                    className="tag-filter-dropdown__clear-link"
                    onClick={() => onSelectedTagsChange([])}
                  >
                    {t('actions.clearSelection')}
                  </button>
                )}
              </>
            )}
            {tags.length === 0 ? (
              <div className="tag-filter-dropdown__empty">{t('tags.noTagsYet')}</div>
            ) : filteredTags.length === 0 ? (
              <div className="tag-filter-dropdown__empty">{t('tags.noMatchingTags')}</div>
            ) : (
              <div className="tag-filter-dropdown__list">
                {filteredTags.map((tag) => (
                  <label key={tag.id} className="tag-filter-dropdown__option">
                    <input
                      type="checkbox"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={() => handleToggleTag(tag.id)}
                      className="tag-filter-dropdown__checkbox"
                    />
                    <span className="tag-filter-dropdown__option-label">{tag.name}</span>
                  </label>
                ))}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
};

export default TagFilterDropdown;
